import { Theme } from '@mui/material/styles';
import { BaseFormComponent, FormTemplate } from './types';
import {
  ProcessTranslator,
  translateFormDifficulty,
  translateTemplateDomain,
  translateFieldFamily,
} from '../processDisplay';

/**
 * TWO VOCABULARIES, NOT ONE (split 2026-08-10)
 * -------------------------------------------
 * Both tabs of this page have a column called `category` and they mean different things:
 *
 *   components → a FIELD FAMILY. What kind of input is this? Production has
 *                Basic (text, multi-line, number, date), Selection (dropdown, multi-select),
 *                Financial (currency), Media (file attachment), HR (worker picker),
 *                Special (signature).
 *   templates  → a BUSINESS DOMAIN. Which part of the company uses this form? Production
 *                has HR (leave, overtime, on/offboarding), Finance (expense, business trip),
 *                Operations (procurement, contract approval).
 *
 * Until now one function and one i18n map (`liveDisplay.formCategories`, 14 keys) served
 * both, which produced two labels the admin cannot tell apart:
 *
 *   "Financial"  the currency FIELD        vs  "Finance"  the finance DEPARTMENT
 *   "HR"         the worker-picker FIELD   vs  "HR"       the HR DEPARTMENT — identical
 *
 * and one shared colour map in which a field family and a department could be assigned the
 * same hue. `TemplateDialog` renders both on ONE screen (the template's domain at the top,
 * its components' families in the body), so this could never be fixed per page — it needed
 * two functions with two maps, chosen at the call site.
 *
 * The stored values are unchanged: a boot-time seeder rewrites these rows on every start, so
 * a migration cannot change their content (svdy-rules §10.9). Only the display splits, which
 * is where the ambiguity was.
 */

/** A component's `category` — what kind of input it is. */
export const getFieldFamilyLabel = (
  t: ProcessTranslator,
  category: BaseFormComponent['category'] | string,
): string => translateFieldFamily(t, category);

/** A template's `category` — which part of the business uses it. */
export const getTemplateDomainLabel = (
  t: ProcessTranslator,
  category: FormTemplate['category'] | string,
): string => translateTemplateDomain(t, category);

/**
 * Field-family hues. Separate from the template map below so a family and a domain cannot
 * silently collide on one colour — `financial` and `finance` did, being one letter apart in a
 * single lookup.
 */
export const getFieldFamilyColor = (theme: Theme, category: string): string => {
  const colors: Record<string, string> = {
    Basic: theme.palette.primary.main,
    Advanced: theme.palette.secondary.main,
    Selection: theme.palette.info.main,
    Financial: theme.palette.success.dark,
    Media: theme.palette.warning.dark,
    HR: theme.palette.secondary.dark,
    Special: theme.palette.warning.main,
  };
  return colors[category] || theme.palette.grey[500];
};

/** Business-domain hues, for templates. */
export const getTemplateDomainColor = (theme: Theme, category: string): string => {
  const colors: Record<string, string> = {
    HR: theme.palette.info.main,
    Finance: theme.palette.error.main,
    IT: theme.palette.primary.dark,
    Operations: theme.palette.secondary.dark,
    Business: theme.palette.success.main,
    General: theme.palette.grey[700],
  };
  return colors[category] || theme.palette.grey[500];
};

export const getDifficultyInfo = (
  t: ProcessTranslator,
  theme: Theme,
  difficulty: 'Easy' | 'Medium' | 'Hard' | string,
): { label: string; color: string } => {
  const label = translateFormDifficulty(t, difficulty);
  switch (difficulty) {
    case 'Easy': return { label, color: theme.palette.success.main };
    case 'Medium': return { label, color: theme.palette.warning.main };
    case 'Hard': return { label, color: theme.palette.error.main };
    default: return { label, color: theme.palette.grey[500] };
  }
};
